import React, { useState } from 'react';
import qs from 'qs';
import { useNavigate, useLocation } from 'react-router-dom';
import { PopoverInteractionKind } from '@blueprintjs/core';
import {
  faSearch,
  faUserPlus,
  faUserCircle,
  faCaretDown,
  faUser,
  faCog,
  faAddressBook,
  faSignOut,
  faUsersCog,
  faListAlt
} from '@fortawesome/pro-solid-svg-icons';
import { Form, FormInstance } from '@lightbeamhealth/blueprint-form';

import { Container, Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import Button from '../../molecules/button';
import Input from '../../molecules/input';
import { Menu, MenuItem, MenuItemLink } from '../menu';
import Tooltip from '../tooltip';
import Popover from '../popover';
import SearchPatientDialog, { DetailedSearchData } from '../dialog/search';
import { useMetaData } from '../../../contexts/metadata';
import { GetJSON } from '../../../libs/request';
import { useAuth } from '../../../contexts/auth';
import { IsInIframe } from '../../../libs/browser';

interface SearchData {
  keyword: string;
}

const TopbarSearch: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (pathname.includes('/patient/search')) {
    return null;
  }
  const form = new FormInstance<SearchData>({
    keyword: ''
  });
  const onSearch = (data: SearchData): void => {
    const keyword = data.keyword.trim();
    if (keyword === '') {
      return;
    }
    navigate(`/patient/search/?keyword=${keyword}`);
    form.getForm().resetForm();
  };
  return (
    <Form form={form} className="form-group-mb-4" onSubmit={onSearch}>
      <Input
        bare
        name="keyword"
        placeholder="Search by Name, Phone, or view by ID or MRN (e.g. ID:123, or MRN: E456)"
        label="Search"
        className="bg-gray4 hover-bg-white rounded bg-none-input shadow-none-input"
        rightElement={
          <Button type="submit" variant="minimal" aria-label="Search Button">
            <Icon icon={faSearch} />
          </Button>
        }
      />
    </Form>
  );
};

const DetailedSearch: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  if (pathname.includes('/patient/search')) {
    return null;
  }
  const onSearch = (data: DetailedSearchData) => {
    setIsOpen(false);
    const params = {};
    for (const key of Object.keys(data)) {
      if (data[key]) {
        params[key] = data[key];
      }
    }
    const query = qs.stringify(params);
    navigate(`/patient/search/?${query}`);
  };
  return (
    <>
      <SearchPatientDialog
        isOpen={isOpen}
        onSearch={onSearch}
        onClose={() => setIsOpen(false)}
      />
      <Tooltip content="Detailed Search">
        <Button
          aria-label="Detailed Search"
          className="bg-gray4-important hover-bg-white-important"
          style={{
            boxShadow: 'none'
          }}
          onClick={() => setIsOpen(true)}
        >
          <Icon icon={faListAlt} />
        </Button>
      </Tooltip>
    </>
  );
};

interface AccountMenuProps {
  isAdminUser: boolean;
  providerTerm: string;
}

const AccountMenu: React.FC<AccountMenuProps> = ({
  isAdminUser,
  providerTerm
}) => {
  const [isAccountExpanded, setIsAccountExpanded] = useState(false);
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const logout = () => {
    GetJSON({
      url: '/logout/'
    }).then(() => {
      navigate('/');
      updateUser(null);
    });
  };
  return (
    <Popover
      placement="auto-end"
      usePortal={false}
      onOpened={() => setIsAccountExpanded(true)}
      onClosed={() => setIsAccountExpanded(false)}
      content={
        <Menu>
          <MenuItemLink href="/profile" text="User Profile" icon={faUser} />
          {isAdminUser && (
            <React.Fragment>
              <MenuItemLink
                href="/configuration"
                text="Account Configuration"
                icon={faCog}
              />
              <MenuItemLink
                href="/users"
                text="Manage Users"
                icon={faUsersCog}
              />
            </React.Fragment>
          )}
          <MenuItemLink
            href="/directory"
            text={`${providerTerm} Directory`}
            icon={faAddressBook}
          />
          <MenuItem
            onClick={logout}
            text="Log Out"
            icon={
              <span className="bp5-icon">
                <Icon className="text-black" icon={faSignOut} />
              </span>
            }
          />
        </Menu>
      }
    >
      <Button
        variant="outlined"
        className="bg-grey ml-4"
        aria-expanded={isAccountExpanded}
      >
        <Icon icon={faUserCircle} className="mr-2 text-white" />
        <span className="font-bold text-white">Account</span>
        <Icon icon={faCaretDown} className="ml-2 text-white" />
      </Button>
    </Popover>
  );
};

interface TopbarProps {
  isAdminUser: boolean;
}

const Topbar: React.FC<TopbarProps> = ({ isAdminUser }) => {
  const { terms } = useMetaData();
  return (
    <div
      className={`bg-grey text-white py-4 w-full${IsInIframe() ? ' hidden' : ''}`}
      style={{
        minHeight: '52px',
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      <Container fluid>
        <Row>
          <Col className="pl-0-important" md={10}>
            <TopbarSearch />
          </Col>
          <Col md={4}>
            <DetailedSearch />
          </Col>
          <Col md={10}>
            <div className="text-right">
              <Tooltip
                enforceFocus={false}
                interactionKind={PopoverInteractionKind.HOVER_TARGET_ONLY}
                content={`Add or prescribe a ${terms.patient.default}`}
              >
                <Button
                  variant="outlined"
                  linkTo="/prescribe/"
                  className="bg-brand-important"
                >
                  <Icon icon={faUserPlus} className="mr-2" />
                  <span className="font-bold">Prescribe</span>
                </Button>
              </Tooltip>
              <AccountMenu
                isAdminUser={isAdminUser}
                providerTerm={terms.provider.capitalized}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Topbar;
